import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/storefront/ProductCard";
import { Heart, Loader2 } from "lucide-react";

export default function Wishlist() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Wait for AuthContext to finish before making any auth decision.
        // Without this, the first render sees user=null and immediately
        // kicks the visitor to /login even though they're actually signed in.
        if (authLoading) return;
        if (!user) {
            navigate("/login?redirect=/wishlist", { replace: true });
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const [wl, cat] = await Promise.all([
                    api.get("/auth/wishlist"),
                    api.get("/catalog/products?limit=100"),
                ]);
                if (cancelled) return;
                const ids = new Set(wl.data || []);
                setItems((cat.data.items || []).filter((p) => ids.has(p.id)));
            } catch (e) {
                // silent — keep empty state
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [user, authLoading, navigate]);

    if (authLoading || loading) {
        return (
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-center" data-testid="wishlist-loading">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-[hsl(var(--accent))]" />
                <p className="mt-3 text-xs font-mono-ui text-muted-foreground tracking-[0.18em] uppercase">Loading your wishlist…</p>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-10" data-testid="wishlist-page">
            <p className="font-mono-ui text-[#857C72]">Saved</p>
            <h1 className="font-display text-5xl mt-2">Wishlist</h1>
            {items.length === 0 ? (
                <div className="mt-10 dw-section p-10 text-center" data-testid="wishlist-empty-state">
                    <Heart className="h-8 w-8 mx-auto text-muted-foreground" />
                    <p className="font-display text-2xl mt-3">Nothing saved yet.</p>
                    <Link to="/shop" className="inline-flex mt-4 dw-btn-primary px-6 py-2 font-mono-ui">Discover drops</Link>
                </div>
            ) : (
                <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="wishlist-grid">
                    {items.map((p, i) => <ProductCard key={p.id} product={p} idx={i} />)}
                </div>
            )}
            <div className="h-24" />
        </div>
    );
}
